import Button from '@ingka/button';
import InputField from '@ingka/input-field';
import trashCan from '@ingka/ssr-icon/paths/trash-can';
import Select, { Option } from '@ingka/select';
import type { SyntheticEvent } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { Participant } from '@lotta/shared/types-lottery-schemas';
import { getYears } from './utils/utils';
import styles from './ParticipantsRow.module.scss';

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export type ParticipantRowProps = {
  onRemoveParticipantsClick: () => void;
  onChange?: (value: Participant) => void;
  onBlur?: () => void;
  disabled: boolean;
  participant: Participant;
};

export const ParticipantRow = ({
  onRemoveParticipantsClick,
  disabled,
  participant,
  onChange,
  onBlur,
}: ParticipantRowProps) => {
  const { t } = useTranslation();

  const { entryDate, partyUid, optedOut } = participant;

  const [year, month, day] = entryDate.split('-');

  const onPartyUidChange = (event: SyntheticEvent<HTMLInputElement>) => {
    if (!onChange) {
      return;
    }

    const { value } = event.target as HTMLInputElement;
    onChange({ ...participant, partyUid: value });
  };

  const onEntryDateChange = (event: SyntheticEvent<HTMLSelectElement>) => {
    if (!onChange) {
      return;
    }

    const { id, value } = event.target as HTMLInputElement;

    let newEntryDate = entryDate;

    switch (id) {
      case 'year':
        newEntryDate = `${value}-${month}-${day}`;
        break;
      case 'month':
        newEntryDate = `${year}-${value}-${day}`;
        break;
      case 'day':
        newEntryDate = `${year}-${month}-${value}`;
        break;
      default:
        newEntryDate = entryDate;
    }

    onChange({ ...participant, entryDate: newEntryDate });
  };

  const years = getYears();
  const daysInMonth = DateTime.now().set({ year: Number(year), month: Number(month) }).daysInMonth;
  const days = [...Array(daysInMonth + 1).keys()].slice(1);

  return (
    <>
      <div className={styles['participant-row']}>
        <div className={styles['input-column']}>
          <InputField
            className={styles['party-uid-input-field']}
            id="partyUid"
            type="text"
            value={partyUid}
            disabled={disabled}
            label={`${t('general-partyuid')}`}
            onChange={onPartyUidChange}
            onBlur={onBlur}
          />
          <div className={styles['date-input-row']}>
            <Select
              className={styles['year-input']}
              id="year"
              label={`${t('general-year')}`}
              disabled={disabled || years.length === 1}
              value={year}
              onChange={(e) => onEntryDateChange(e)}
              onBlur={onBlur}
            >
              {years.map((year) => (
                <Option value={year.toString(10)} key={year} name={year.toString(10)} />
              ))}
            </Select>
            <Select
              className={styles['month-input']}
              id="month"
              label={`${t('general-month')}`}
              disabled={disabled}
              value={month}
              onChange={(e) => onEntryDateChange(e)}
              onBlur={onBlur}
              hintText={''}
            >
              {MONTHS.map((element) => {
                const monthString = element.toLocaleString('en-US', {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                });

                return <Option value={monthString} key={monthString} name={monthString} />;
              })}
            </Select>
            <Select
              className={styles['day-input']}
              id="day"
              label={`${t('general-day')}`}
              disabled={disabled}
              value={day}
              onBlur={onBlur}
              onChange={(e) => onEntryDateChange(e)}
              hintText={''}
            >
              {days.map((element) => {
                const dayString = element.toLocaleString('en-US', {
                  minimumIntegerDigits: 2,
                  useGrouping: false,
                });

                return <Option value={dayString} key={dayString} name={dayString} />;
              })}
            </Select>
          </div>
        </div>
        <Button
          small
          type="tertiary"
          iconOnly
          ssrIcon={trashCan}
          onClick={onRemoveParticipantsClick}
        />
      </div>
      {optedOut && (
        <div>{t('lottery-details-manage-manual-participants-participant-opted-out')}</div>
      )}
      <hr className="divider" />
    </>
  );
};
